import React from 'react';
import { useTranslation } from 'react-i18next';
import { PricingTeaserMessages, PricingTeaserText } from './PricingTeaser.messages';
import { PlanNameMessages } from '../common/PlanName.messages';
import { useBilling } from '../../context/BillingContext';
import { useUser } from '../../context/UserContext';
import { PlanFreeIcon, PlanStarterIcon, PlanGrowthIcon, PlanScaleIcon } from '../../design-system/components/SvgIcons';

interface PricingTeaserProps {
  onSeePricing: () => void;
}

export const PricingTeaser: React.FC<PricingTeaserProps> = ({ onSeePricing }) => {
  const { t } = useTranslation();
  const user = useUser();
  const { isPaid, isTrialActive, subscription } = useBilling();
  const isAuthenticated = !!user.email;
  const currentPlanKey = (subscription as any)?.activePlan || (subscription as any)?.planName;

  const plans = [
    { key: 'trial', name: PlanNameMessages.FREE, price: PricingTeaserMessages.PLAN_TRIAL_PRICE, period: PricingTeaserMessages.PLAN_TRIAL_PERIOD, words: PricingTeaserMessages.PLAN_TRIAL_WORDS, popular: false },
    { key: 'starter', name: PlanNameMessages.STARTER, price: PricingTeaserMessages.PLAN_STARTER_PRICE, period: PricingTeaserMessages.PLAN_STARTER_PERIOD, words: PricingTeaserMessages.PLAN_STARTER_WORDS, popular: false },
    { key: 'growth', name: PlanNameMessages.GROWTH, price: PricingTeaserMessages.PLAN_GROWTH_PRICE, period: PricingTeaserMessages.PLAN_GROWTH_PERIOD, words: PricingTeaserMessages.PLAN_GROWTH_WORDS, popular: true },
    { key: 'scale', name: PlanNameMessages.SCALE, price: PricingTeaserMessages.PLAN_SCALE_PRICE, period: PricingTeaserMessages.PLAN_SCALE_PERIOD, words: PricingTeaserMessages.PLAN_SCALE_WORDS, popular: false },
  ];

  const features = [
    PricingTeaserMessages.FEATURE_LOCALES,
    PricingTeaserMessages.FEATURE_STORAGE,
    PricingTeaserMessages.FEATURE_CLI,
    PricingTeaserMessages.FEATURE_API,
  ];

  const planIcons: Record<string, React.ReactNode> = {
    trial: <PlanFreeIcon size={36} />,
    starter: <PlanStarterIcon size={36} />,
    growth: <PlanGrowthIcon size={36} />,
    scale: <PlanScaleIcon size={36} />,
  };

  return (
    <section className="pt-section">
      <div className="container">
        <div className="section-header">
          <h2>{t(PricingTeaserMessages.TITLE)}</h2>
          <p className="section-subtitle">{t(PricingTeaserMessages.SUBTITLE)}</p>
        </div>

        <div className="pt-grid">
          {plans.map((plan) => {
            const isCurrent = (isPaid && plan.key === currentPlanKey) || (isTrialActive && plan.key === 'trial');
            const classes = ['pt-card', plan.popular && 'pt-card-popular', isCurrent && 'pt-card-current'].filter(Boolean).join(' ');
            return (
              <div key={plan.key} className={classes}>
                {isCurrent && <span className="pt-badge pt-badge-current">Current Plan</span>}
                {!isCurrent && plan.popular && <span className="pt-badge pt-badge-popular">{t(PricingTeaserMessages.MOST_POPULAR)}</span>}
                <div style={{ marginBottom: '8px' }}>{planIcons[plan.key]}</div>
                <h4 className="pt-plan-name">{t(plan.name)}</h4>
                <div className="pt-price">
                  <span className="pt-price-amount">{t(plan.price)}</span>
                  <span className="pt-price-period">{t(plan.period)}</span>
                </div>
                <p className="pt-words">{t(plan.words)}</p>
                <div className="pt-features">
                  <p className="pt-features-label">
                    {t(PricingTeaserMessages.FEATURES_LABEL, { defaultValue: PricingTeaserText.FEATURES_LABEL })}
                  </p>
                  {features.map((feat, j) => (
                    <p key={j} className="pt-feature">✓ {t(feat)}</p>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="pt-highlights">
          <span>✓ {t(PricingTeaserMessages.HIGHLIGHT_FREE_TRIAL)}</span>
          <span>✓ {t(PricingTeaserMessages.HIGHLIGHT_NO_CARD)}</span>
          <span>✓ {t(PricingTeaserMessages.HIGHLIGHT_CANCEL)}</span>
          <span>✓ {t(PricingTeaserMessages.HIGHLIGHT_ANNUAL)}</span>
        </div>

        <div className="pt-cta">
          <button className="btn btn-primary btn-large" onClick={onSeePricing}>
            {isAuthenticated ? t(PricingTeaserMessages.BTN_GO_TO_DASHBOARD) : t(PricingTeaserMessages.BTN_START_FREE_TRIAL)}
          </button>
        </div>
      </div>
    </section>
  );
};
